import { useState } from "react";
import { useAuth } from "@/lib/auth.jsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { flushProgressAfterAuth } from "@/lib/progress";
import { trackConversion } from "@/lib/analytics";

export default function AuthPage() {
  const { login, loginWithToken } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/quiz";
  const refFromUrl = sp.get("ref") || "";
  const [mode, setMode] = useState("register");
  const [email, setEmail] = useState(() => localStorage.getItem("ihmj_lead_email") || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [refCode, setRefCode] = useState(refFromUrl);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [pronouns, setPronouns] = useState("");
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);

  const sendReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If that email has an account, we just sent a reset link. Check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally { setLoading(false); }
  };

  const codeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!codeSent) {
        await api.post("/auth/request-code", { email });
        setCodeSent(true);
        toast.success("We sent a 6-digit sign-in code to your email.");
      } else {
        const { data } = await api.post("/auth/login-code", { email, code });
        loginWithToken(data);
        const dest = await flushProgressAfterAuth(next);
        toast.success("You're in.");
        nav(dest);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        const dest = await flushProgressAfterAuth(next);
        toast.success("You're in.");
        nav(dest);
      } else {
        await api.post("/auth/register", { email, password, name, referral_code: refCode || undefined, marketing_opt_in: marketingOptIn, pronouns: pronouns || undefined });
        try {
          await login(email, password);
        } catch (e) {
          // Non-enumerable: existing email (wrong password) looks identical to a fresh signup.
          toast.message("Check your email to finish, or sign in if you already have an account.");
          setMode("login");
          return;
        }
        try { await trackConversion(); } catch { /* non-critical */ }
        const dest = await flushProgressAfterAuth(next);
        window.location.href = dest;
        return;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally { setLoading(false); }
  };

  if (mode === "forgot") {
    return (
      <div className="max-w-md mx-auto px-5 pt-12">
        <h1 className="font-display text-3xl sm:text-4xl font-black" data-testid="auth-title">Reset your password</h1>
        <p className="text-gray-300 mt-3 text-sm sm:text-base leading-relaxed">Enter your email and we'll send you a secure link to set a new password. It expires in 60 minutes.</p>
        <form onSubmit={sendReset} className="mt-8 space-y-4">
          <input value={email} onChange={e=>setEmail(e.target.value)} type="email" required placeholder="Email" className="w-full bg-[#14161B] border border-[#2A2E39] rounded-lg px-4 py-3 focus:border-[#FD0F10] outline-none" data-testid="auth-forgot-email" />
          <button disabled={loading} className="w-full hero-accent text-white font-display font-bold text-lg py-3.5 rounded-lg disabled:opacity-60" data-testid="auth-forgot-submit">{loading ? "..." : "SEND RESET LINK"}</button>
        </form>
        <button className="mt-6 text-sm text-gray-400 hover:text-white" onClick={()=>setMode("login")} data-testid="auth-back-signin">Back to sign in</button>
      </div>
    );
  }

  if (mode === "code") {
    return (
      <div className="max-w-md mx-auto px-5 pt-12">
        <h1 className="font-display text-3xl sm:text-4xl font-black" data-testid="auth-title">Sign in with a code</h1>
        <p className="text-gray-300 mt-3 text-sm sm:text-base leading-relaxed">{codeSent ? "Enter the 6-digit code we emailed you. It expires in 10 minutes." : "No password needed. We'll email you a 6-digit code to sign in."}</p>
        <form onSubmit={codeSubmit} className="mt-8 space-y-4">
          <input value={email} onChange={e=>setEmail(e.target.value)} type="email" required disabled={codeSent} placeholder="Email" className="w-full bg-[#14161B] border border-[#2A2E39] rounded-lg px-4 py-3 focus:border-[#FD0F10] outline-none disabled:opacity-60" data-testid="auth-code-email" />
          {codeSent && (
            <input value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,"").slice(0,6))} inputMode="numeric" required placeholder="6-digit code" className="w-full bg-[#14161B] border border-[#2A2E39] rounded-lg px-4 py-3 focus:border-[#FD0F10] outline-none tracking-[0.5em] text-center font-mono-ui text-xl" data-testid="auth-code-input" />
          )}
          <button disabled={loading} className="w-full hero-accent text-white font-display font-bold text-lg py-3.5 rounded-lg disabled:opacity-60" data-testid={codeSent ? "auth-code-verify" : "auth-code-send"}>{loading ? "..." : codeSent ? "SIGN IN" : "EMAIL ME A CODE"}</button>
        </form>
        {codeSent && (
          <button className="mt-4 text-sm text-gray-400 hover:text-white" onClick={()=>{setCodeSent(false);setCode("");}} data-testid="auth-code-restart">Use a different email</button>
        )}
        <div className="mt-6">
          <button className="text-sm text-gray-400 hover:text-white" onClick={()=>{setMode("login");setCodeSent(false);setCode("");}} data-testid="auth-back-signin">Back to sign in</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-5 pt-12">
      <h1 className="font-display text-3xl sm:text-4xl font-black" data-testid="auth-title">
        {mode === "login" ? "Welcome back. Let's pick this up." : "Good. You're here. Let's build."}
      </h1>
      <p className="text-gray-300 mt-3 text-sm sm:text-base leading-relaxed">
        {mode === "login"
          ? "Right where you left off. We're still on your side."
          : "Under three minutes. You will walk out with a personal escape plan built for your actual life. Your money, your time, your personality. No hype. No fluff. Just your next real move."}
      </p>
      {mode === "register" && (
        <p className="text-xs text-[#FD0F10] mt-3 font-mono-ui uppercase tracking-wider">Free to sign up · Free plan · No card · No spam · Start today</p>
      )}
      <form onSubmit={submit} className="mt-8 space-y-4">
        {mode === "register" && (
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="Your first name" className="w-full bg-[#14161B] border border-[#2A2E39] rounded-lg px-4 py-3 focus:border-[#FD0F10] outline-none" data-testid="auth-name" />
        )}
        <input value={email} onChange={e=>setEmail(e.target.value)} type="email" required placeholder="Email" className="w-full bg-[#14161B] border border-[#2A2E39] rounded-lg px-4 py-3 focus:border-[#FD0F10] outline-none" data-testid="auth-email" />
        <input value={password} onChange={e=>setPassword(e.target.value)} type="password" required minLength={6} placeholder="Password (6+ chars)" className="w-full bg-[#14161B] border border-[#2A2E39] rounded-lg px-4 py-3 focus:border-[#FD0F10] outline-none" data-testid="auth-password" />
        {mode === "register" && (
          <div>
            <label className="text-xs font-mono-ui uppercase tracking-wider text-gray-400">How should we address you? (optional)</label>
            <select value={pronouns} onChange={e => setPronouns(e.target.value)} className="w-full mt-1 bg-[#14161B] border border-[#2A2E39] rounded-lg px-4 py-3 focus:border-[#FD0F10] outline-none" data-testid="auth-pronouns">
              <option value="">Choose one (optional)</option>
              <option value="Mr.">Mr.</option>
              <option value="Miss">Miss</option>
              <option value="Mrs.">Mrs.</option>
              <option value="first_name">Just use my first name</option>
            </select>
            <p className="mt-1 text-xs text-gray-400">So we address you the way you want to be addressed.</p>
          </div>
        )}
        {mode === "register" && (
          <input value={refCode} onChange={e=>setRefCode(e.target.value.toUpperCase())} placeholder="Referral code (optional)" className="w-full bg-[#14161B] border border-[#2A2E39] rounded-lg px-4 py-3 focus:border-[#FD0F10] outline-none font-mono-ui tracking-widest" data-testid="auth-ref" />
        )}
        {mode === "register" && (
          <label className="flex items-start gap-3 text-sm text-gray-300 cursor-pointer select-none py-1" data-testid="auth-marketing-label">
            <input type="checkbox" checked={marketingOptIn} onChange={e => setMarketingOptIn(e.target.checked)} className="accent-[#FD0F10] scale-125 mt-0.5" data-testid="auth-marketing-optin" />
            <span>I agree to receive updates and other offers.</span>
          </label>
        )}
        <button disabled={loading} className="w-full hero-accent text-white font-display font-bold text-lg py-3.5 rounded-lg disabled:opacity-60" data-testid="auth-submit">
          {loading ? "..." : mode === "login" ? "SIGN IN" : "SIGN UP TODAY, BUILD MY PLAN"}
        </button>
      </form>
      {mode === "register" && (
        <p className="mt-4 text-xs text-gray-400 leading-relaxed">
          Next you will answer eleven quick questions and unlock your personal escape plan. We only ask what we need to build it. Your answers stay yours. Let's go.
        </p>
      )}
      <button className="mt-6 text-sm text-gray-400 hover:text-white" onClick={()=>setMode(mode === "login" ? "register" : "login")} data-testid="auth-toggle">
        {mode === "login" ? "New here? Create an account." : "Already have an account? Sign in."}
      </button>
      {mode === "login" && (
        <div className="mt-4 flex flex-col gap-2">
          <button className="text-sm text-[#FD0F10] hover:underline text-left" onClick={()=>setMode("forgot")} data-testid="auth-forgot-link">Forgot password?</button>
          <button className="text-sm text-gray-400 hover:text-white text-left" onClick={()=>{setMode("code");setCodeSent(false);setCode("");}} data-testid="auth-code-link">Email me a sign-in code instead</button>
        </div>
      )}
    </div>
  );
}
